import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Menu, X, Search, ChevronDown, Settings, LogOut, Store } from 'lucide-react';
import { logout } from '../../redux/slice/authSlice';
import './Navbar.css';

const routeTitleMap = {
  '/dashboard': 'Dashboard Overview',
  '/bookings': 'Booking Management',
  '/shops': 'Shops & Services',
  '/analytics': 'Performance Analytics',
  '/settings': 'Vendor Settings',
};

const DashboardNavbar = ({ onMenuClick, menuOpen }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const dropdownRef = useRef(null);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;
  let currentTitle = 'Dashboard Overview';
  if (currentPath.startsWith('/shops/')) {
    currentTitle = 'Shop Services Catalog';
  } else if (routeTitleMap[currentPath]) {
    currentTitle = routeTitleMap[currentPath];
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showUserMenu]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    dispatch(logout());
    navigate('/login');
  };

  const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : 'V';

  return (
    <header className="dashboard-navbar">
      <div className="dashboard-navbar-content">
        {/* Left Section: Mobile Toggle & Route Breadcrumbs */}
        <div className="dashboard-nav-left">
          {onMenuClick ? (
            <button
              type="button"
              className="dashboard-menu-toggle"
              aria-label={menuOpen ? 'Close sidebar' : 'Open sidebar'}
              aria-expanded={!!menuOpen}
              onClick={onMenuClick}
            >
              {menuOpen ? <X size={16} /> : <Menu size={16} />}
            </button>
          ) : null}

          {/* Breadcrumb / Title */}
          <div className="dashboard-breadcrumbs">
            <span className="breadcrumb-root">Vendor</span>
            <span className="breadcrumb-separator">/</span>
            <h1 className="breadcrumb-current">{currentTitle}</h1>
          </div>
        </div>

        {/* Center / Search bar */}
        <div className="dashboard-search-wrapper">
          <div className="dashboard-search-bar" onClick={() => navigate('/bookings')}>
            <Search size={14} className="search-icon" />
            <span className="search-placeholder">Quick search bookings, shops, services…</span>
            <span className="search-shortcut">⌘K</span>
          </div>
        </div>

        {/* Right Section */}
        <div className="dashboard-nav-right">
          {/* Live Status Pill */}
          <div className="system-status-pill" title="Backend operational">
            <span className="status-dot"></span>
            <span className="status-text">Live Core</span>
          </div>

          {/* User Menu Dropdown */}
          <div className="dashboard-user-menu" ref={dropdownRef}>
            <button
              className="dashboard-user-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-expanded={showUserMenu}
            >
              <div className="user-avatar-pill">{userInitial}</div>
              <div className="user-text-pill">
                <span className="user-name-text">{user?.name || 'Vendor'}</span>
                <span className="user-role-badge">Shop Partner</span>
              </div>
              <ChevronDown
                size={14}
                className={`dropdown-arrow ${showUserMenu ? 'dropdown-arrow-open' : ''}`}
              />
            </button>

            {showUserMenu && (
              <div className="dashboard-user-dropdown">
                <div className="dropdown-user-header">
                  <div className="dropdown-avatar-lg">{userInitial}</div>
                  <div className="dropdown-user-meta">
                    <span className="dropdown-name">{user?.name || 'Partner Account'}</span>
                    <span className="dropdown-email">{user?.email || 'vendor@b4barber.com'}</span>
                    <span className="dropdown-role-tag">
                      <Store size={11} /> Verified Partner
                    </span>
                  </div>
                </div>

                <div className="dropdown-menu-list">
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/settings');
                    }}
                  >
                    <Settings size={14} />
                    <span>Profile &amp; Settings</span>
                  </button>

                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/shops');
                    }}
                  >
                    <Store size={14} />
                    <span>My Barbershops</span>
                  </button>

                  <div className="dropdown-divider" />

                  <button className="dropdown-item logout-btn" onClick={handleLogout}>
                    <LogOut size={14} />
                    <span>Sign out of portal</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardNavbar;
